import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GanttChart, Archive, Compass, Contact, ArrowLeft, BellRing, FolderInput, BookmarkPlus, CalendarDays, Camera, ChartColumn, Check, ChevronDown, ClipboardList, Columns3, Download, LayoutDashboard, LayoutGrid, LayoutTemplate, List, FileDown, ListFilter, MoreHorizontal, Palette, Paperclip, Pencil, Plus, Table2, Tag, Upload, Users, Webhook, Zap } from 'lucide-react';
import { dependencyConflicts, openBlockers, activeFilterCount, byPosition, CARD_SORT_LABEL, isFilterActive, matchesFilter, roleAtLeast, BUILTIN_TEMPLATES, pastedTasks, type CardSummary, type Member, type ParsedImport } from '@kaba/shared';
import { api, newOpId } from '../../lib/api';
import { useMe } from '../../lib/auth';
import { archiveCard, boardKey, editedLocally, restoreCard, queryClient, upsertCards, useBoard, useBoardAction, useBoards, useCreateCard } from '../../lib/board';
import { deviceClass, noteBoardOpened, preferredView, rememberView, useToggleFavorite, VIEWS, type View } from '../../lib/boardNav';
import { BoardSwitcher, FavoriteButton } from './BoardSwitcher';
import { BoardOverview } from '../dashboard/DashboardView';
import { ContextButton, ContextSection, ContextSheet } from './BoardContext';
import { ParticipantsSheet } from './Participants';
import { openContext } from '../../lib/context';
import { FilesView } from './FilesView';
import { ActiveFilters, FilterBar, useViewConfig } from './FilterBar';
import { undoFilterChange } from './undo';
import { useBoardRealtime, usePresence, useViewingCard } from '../../lib/realtime';
import { navigate, setQuery, useLocation } from '../../lib/router';
import { closeCard, openCard } from '../../lib/cardNav';
import { FormsSheet } from '../forms/FormsSheet';
import { BrandLogo } from '../BrandLogo';
import { useBoardBranding, useThemeMode } from '../../lib/theme';
import { backgroundStyle } from '../../lib/background';
import { useKeyboardOpen } from '../../lib/useKeyboardOpen';
import { toast } from '../../lib/toast';
import { InboxButton } from '../Inbox';
import { PromptDialog } from '../ui/Prompt';
import { Avatar, Button, cx, DropdownContent, DropdownItem, DropdownMenu, DropdownSeparator, DropdownTrigger, IconButton, Sheet, SyncStatus } from '../ui/ui';
import { BrandingDialog } from '../branding/BrandingDialog';
import { ExportDialog } from './ExportDialog';
import { BoardDnd } from './BoardDnd';
import { CalendarView } from './CalendarView';
import { TimelineView } from './TimelineView';
import { TableView } from './TableView';
import { MetricsView } from './MetricsView';
import { AutomationsSheet } from './Automations';
import { CardTemplatesSheet, SaveBoardTemplateDialog } from './Templates';
import { useLaneSetup } from './Lanes';
import { CalendarFeedSheet, WebhooksSheet } from '../Integrations';
import { BoardColumn, CardMenu, CardTile, ListGroup, ListRow } from './Columns';
import { MembersDialog } from './MembersDialog';
import { MoveBoardDialog } from '../home/MoveBoardDialog';
import { BoardReminders } from '../card/Reminders';
import { refreshStructure } from '../../lib/structure';
import { MoveDialog } from './MoveDialog';
import { LabelsDialog } from './LabelsDialog';
import { CardImportDialog } from './CardImportDialog';
import { PasteTasksPrompt } from './PasteTasksPrompt';
import { openCardImport } from '../../lib/cardImport';

// Kartendetails (inkl. Editor) erst laden, wenn eine Karte geöffnet wird
const CardDetailSheet = lazy(() => import('../card/CardDetail').then((m) => ({ default: m.CardDetailSheet })));

const VIEW_META: Record<View, [string, typeof Columns3]> = {
  board: ['Board', Columns3],
  list: ['Liste', List],
  table: ['Tabelle', Table2],
  calendar: ['Kalender', CalendarDays],
  timeline: ['Zeitleiste', GanttChart],
  files: ['Dateien', Paperclip],
  overview: ['Übersicht', LayoutDashboard],
  metrics: ['Kennzahlen', ChartColumn],
};
/** Immer sichtbar; der Rest steht unter „Weitere“ (am Handy auch die Tabelle) */
const PRIMARY_DESKTOP: View[] = ['board', 'list', 'table'];
const PRIMARY_PHONE: View[] = ['board', 'list'];

export function BoardPage({ boardId }: { boardId: string }) {
  const { data: snapshot, isLoading, error } = useBoard(boardId);
  const { data: me } = useMe();
  const { query } = useLocation();
  useBoardRealtime(boardId, !!snapshot);
  useViewingCard(boardId, query.get('card'));
  const presence = usePresence(boardId);
  const onlineIds = useMemo(() => new Set(presence.map((p) => p.userId)), [presence]);
  // Karte → andere Personen, die sie gerade offen haben
  const viewersByCard = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const p of presence) {
      if (p.userId === me?.id) continue;
      for (const id of p.cardIds) m.set(id, [...(m.get(id) ?? []), p.userId]);
    }
    return m;
  }, [presence, me?.id]);

  const qView = query.get('view') as View | null;
  const layout: View = qView && VIEWS.includes(qView) ? qView : preferredView(boardId);
  const openCardId = query.get('card');
  const setLayout = useCallback(
    (l: View) => {
      rememberView(boardId, l);
      setQuery({ view: l }, { replace: true });
    },
    [boardId],
  );
  useEffect(() => noteBoardOpened(boardId), [boardId]);
  // Markenfarbe des Boards – auch für Dialoge in Portalen; beim Verlassen zurück zur Installation (B03, V03)
  useBoardBranding(snapshot?.branding);
  const themeMode = useThemeMode();
  const { data: boards } = useBoards();
  const thisBoard = boards?.find((b) => b.id === boardId);
  const toggleFavorite = useToggleFavorite();
  const [switcher, setSwitcher] = useState(false);

  const { config, update: updateConfig, setFilter } = useViewConfig();
  const filterActive = isFilterActive(config.filter);
  // Die Leiste ist nur das Werkzeug; gesetzte Filter stehen auch bei geschlossener Leiste als Chips da
  const [filterOpen, setFilterOpen] = useState(false);
  const [moveCardId, setMoveCardId] = useState<string | null>(null);
  const [labelsOpen, setLabelsOpen] = useState(false);
  const [membersOpen, setMembersOpen] = useState(false);
  const [placeOpen, setPlaceOpen] = useState(false);
  const [remindersOpen, setRemindersOpen] = useState(false);
  const [quickAdd, setQuickAdd] = useState(false);
  const keyboardOpen = useKeyboardOpen();
  const [renameOpen, setRenameOpen] = useState(false);
  // 0.89 (K1): „Worum es geht“ – Boardkopf, Menü und Boardübersicht öffnen dasselbe Panel
  const [ctxOpen, setCtxOpen] = useState<{ edit: boolean } | null>(null);
  useEffect(() => {
    const on = (e: Event) => {
      const d = (e as CustomEvent<{ boardId: string; edit: boolean }>).detail;
      if (d.boardId === boardId) setCtxOpen({ edit: d.edit });
    };
    window.addEventListener('kaba-context-open', on);
    return () => window.removeEventListener('kaba-context-open', on);
  }, [boardId]);
  // 0.90 (K3): Beteiligte ohne Konto – Board-Menü und Kartenabschnitt öffnen dasselbe Panel
  const [partOpen, setPartOpen] = useState(false);
  useEffect(() => {
    const on = (e: Event) => (e as CustomEvent<{ boardId: string }>).detail.boardId === boardId && setPartOpen(true);
    window.addEventListener('kaba-participants-open', on);
    return () => window.removeEventListener('kaba-participants-open', on);
  }, [boardId]);
  const [brandingOpen, setBrandingOpen] = useState(false);
  /** Export-Dialog: undefined = zu, null = ohne Auswahl, Array = ausgewählte Karten */
  const [exportIds, setExportIds] = useState<string[] | null | undefined>(undefined);
  const [panel, setPanel] = useState<'automations' | 'cardTemplates' | 'saveTemplate' | 'calendar' | 'webhooks' | 'forms' | null>(() =>
    new URLSearchParams(location.search).get('formulare') ? 'forms' : null,
  );
  const [addColumn, setAddColumn] = useState(false);
  const scroller = useRef<HTMLDivElement>(null);
  const boardAction = useBoardAction(boardId);

  // Tastenkürzel: Strg/Cmd+B wechselt Board/Liste, "/" fokussiert Suche
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      if (t.closest('input, textarea, select, [contenteditable="true"]')) return;
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'b') {
        e.preventDefault();
        setLayout(layout === 'board' ? 'list' : 'board');
      } else if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSwitcher(true);
      } else if (e.key === '/') {
        e.preventDefault();
        setFilterOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [layout, setLayout]);

  const labelNames = useMemo(() => new Map((snapshot?.labels ?? []).map((l) => [l.id, l.name])), [snapshot?.labels]);
  const filter = config.filter;
  const visible = useCallback(
    (c: CardSummary) => matchesFilter(c, filter, { meId: me?.id, labelNames }),
    [filter, me?.id, labelNames],
  );
  // Abhängigkeiten: offene Vorgänger und Terminkonflikte je Karte
  const depInfo = useMemo(() => {
    const cards = snapshot?.cards ?? [];
    const byId = new Map(cards.map((c) => [c.id, c]));
    const conflicts = new Set(dependencyConflicts(cards).map((x) => x.cardId));
    const m = new Map<string, { waiting: number; conflict: boolean }>();
    for (const c of cards) {
      const n = openBlockers(c, byId).length;
      if (n) m.set(c.id, { waiting: n, conflict: conflicts.has(c.id) });
    }
    return m;
  }, [snapshot?.cards]);
  const visibleCards = useMemo(() => (snapshot?.cards ?? []).filter(visible), [snapshot?.cards, visible]);
  const shownCount = visibleCards.length;
  const canEditLanes = !!snapshot && roleAtLeast(snapshot.board.role ?? 'viewer', 'editor');
  const lanes = useLaneSetup({
    snapshot,
    mode: layout === 'board' || layout === 'list' ? config.lanes : undefined,
    emptyLanes: !!config.emptyLanes,
    layout: layout === 'list' ? 'list' : 'board',
    visibleCards,
    canEdit: canEditLanes,
  });

  // Hinweis, wenn eine gerade selbst bearbeitete Karte aus dem Filter fällt (mit Rückgängig und Öffnen)
  const prevCards = useRef<{ filter: typeof filter; cards: Map<string, CardSummary> } | null>(null);
  useEffect(() => {
    if (!snapshot) return;
    const prev = prevCards.current;
    prevCards.current = { filter, cards: new Map(snapshot.cards.map((c) => [c.id, c])) };
    if (!prev || prev.filter !== filter || !isFilterActive(filter)) return;
    for (const c of snapshot.cards) {
      const before = prev.cards.get(c.id);
      if (!before || !visible(before) || visible(c)) continue;
      if (!editedLocally(c.id) && query.get('card') !== c.id) continue;
      toast.info(`„${c.title}“ passt nicht mehr zum Filter und ist ausgeblendet.`, [
        { label: 'Rückgängig', run: () => void undoFilterChange(boardId, before, c) },
        { label: 'Öffnen', run: () => openCard(c.id) },
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshot?.cards, filter]);

  const labels = useMemo(() => new Map((snapshot?.labels ?? []).map((l) => [l.id, l])), [snapshot?.labels]);
  const members = useMemo(() => new Map((snapshot?.members ?? []).map((m) => [m.id, m])), [snapshot?.members]);
  const columns = useMemo(() => [...(snapshot?.columns ?? [])].sort(byPosition), [snapshot?.columns]);
  const cardFields = useMemo(() => (snapshot?.fields ?? []).filter((f) => f.showOnCard), [snapshot?.fields]);
  // Stabile Werte für die Kartenkacheln (memo): sonst zeichnet jede Änderung alle Karten neu
  const viewerMembers = useMemo(() => {
    const m = new Map<string, Member[]>();
    for (const [id, ids] of viewersByCard) {
      const list = ids.map((u) => members.get(u)).filter((x): x is Member => !!x);
      if (list.length) m.set(id, list);
    }
    return m;
  }, [viewersByCard, members]);
  const menuActions = useRef<{ move: (id: string) => void; archive: (id: string) => void }>({ move: () => {}, archive: () => {} });
  const renderMenu = useCallback(
    (card: CardSummary, onImage: boolean) => (
      <CardMenu
        card={card}
        onImage={onImage}
        onOpen={() => openCard(card.id)}
        onMove={() => menuActions.current.move(card.id)}
        onArchive={() => menuActions.current.archive(card.id)}
      />
    ),
    [],
  );

  if (isLoading) return <div className="p-8 text-sm text-[var(--ink-soft)]">Lade Board …</div>;
  // Mit Stand vom Gerät weiterarbeiten, auch wenn das Nachladen gerade scheitert (offline, 0.44)
  if (!snapshot)
    return (
      <div className="flex flex-col items-start gap-3 p-8">
        <p>
          {(error as { status?: number } | null)?.status === 0
            ? 'Dieses Board ist offline nicht verfügbar – es wurde auf diesem Gerät noch nicht geöffnet.'
            : 'Dieses Board gibt es nicht oder du hast keinen Zugriff.'}
        </p>
        <Button onClick={() => navigate('/')}>Zur Übersicht</Button>
      </div>
    );

  const canEdit = roleAtLeast(snapshot.board.role, 'editor');
  const othersOnline = [...onlineIds].filter((id) => id !== me?.id).length;
  const archive = (id: string) =>
    boardAction.mutate(async () => {
      const before = await archiveCard(queryClient, boardId, id);
      toast.info('Karte archiviert.', {
        label: 'Rückgängig',
        run: () => boardAction.mutate(() => restoreCard(queryClient, boardId, before, id)),
      });
    });

  menuActions.current = { move: setMoveCardId, archive };
  const menu = canEdit ? renderMenu : undefined;

  const scrollToColumn = (id: string) =>
    document.getElementById(`col-${id}`)?.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });

  return (
    <div className="flex h-full flex-col">
      {/* ---------- Kopf: Zeile 1 Board, Zeile 2 Ansicht und Filter ---------- */}
      <header
        className={cx(
          'pt-safe sticky top-0 z-20 border-b border-[var(--line)] backdrop-blur',
          snapshot.branding?.header === 'tinted' ? 'bg-[var(--color-header-tint)]' : 'bg-[var(--surface)]/95',
        )}
      >
        <div className="flex items-center gap-1 px-2 pt-1.5 sm:px-3">
          <IconButton label="Zur Übersicht" onClick={() => navigate('/')}>
            <ArrowLeft size={20} />
          </IconButton>
          {snapshot.branding?.logo && <BrandLogo logo={snapshot.branding.logo} name={`Logo von ${snapshot.board.title}`} className="mx-1 shrink-0" />}
          <button
            className="inline-flex min-h-11 min-w-0 flex-1 items-center gap-1 rounded-lg px-1.5 text-left hover:bg-black/5 sm:min-h-10 sm:flex-none dark:hover:bg-white/5"
            onClick={() => setSwitcher(true)}
            aria-haspopup="dialog"
            aria-label={`${snapshot.board.title} – Board wechseln (Strg/Cmd+K)`}
            title="Board wechseln (Strg/Cmd+K)"
          >
            <h1 className="truncate text-lg font-semibold">{snapshot.board.title}</h1>
            <ChevronDown size={18} className="shrink-0 text-[var(--ink-soft)]" aria-hidden />
          </button>
          {thisBoard && <FavoriteButton board={thisBoard} onToggle={(favorite) => toggleFavorite.mutate({ id: boardId, favorite })} />}
          <ContextButton boardId={boardId} onOpen={() => setCtxOpen({ edit: false })} />
          {/* Speicherstatus des ganzen Boards; am Handy nur das Symbol (Offline/Fehler mit Wort) */}
          <div className="min-w-0 sm:px-1">
            <SyncStatus />
          </div>
          <div className="ml-auto flex shrink-0 items-center gap-1">
            <InboxButton />
            <button
              onClick={() => setMembersOpen(true)}
              className="hidden items-center -space-x-1.5 rounded-lg px-1.5 py-1 hover:bg-black/5 sm:flex dark:hover:bg-white/5"
              aria-label={othersOnline ? `Mitglieder – ${othersOnline} gerade da` : 'Mitglieder'}
            >
              {[...snapshot.members]
                .sort((a, b) => Number(onlineIds.has(b.id) && b.id !== me?.id) - Number(onlineIds.has(a.id) && a.id !== me?.id))
                .slice(0, 4)
                .map((m) => (
                  <Avatar key={m.id} user={m} size={28} online={m.id !== me?.id && onlineIds.has(m.id)} />
                ))}
            </button>
            <IconButton
              label={othersOnline ? `Mitglieder – ${othersOnline} gerade da` : 'Mitglieder'}
              onClick={() => setMembersOpen(true)}
              className="relative max-[399px]:hidden sm:hidden"
            >
              <Users size={19} />
              {othersOnline > 0 && <span className="absolute top-2 right-2 size-2.5 rounded-full bg-emerald-500 ring-2 ring-[var(--surface)]" />}
            </IconButton>
            <DropdownMenu>
              <DropdownTrigger asChild>
                <IconButton label="Board-Menü">
                  <MoreHorizontal size={19} />
                </IconButton>
              </DropdownTrigger>
              <DropdownContent>
                {/* Gruppen: Gestaltung und Ablage · Zusammenarbeit und Regeln · Integrationen · Archivierung (U01) */}
                {snapshot.board.role === 'owner' && !me?.isGuest && (
                  <DropdownItem icon={<Palette size={16} />} onSelect={() => setBrandingOpen(true)}>
                    Erscheinungsbild
                  </DropdownItem>
                )}
                <DropdownItem icon={<Compass size={16} />} onSelect={() => setCtxOpen({ edit: false })}>
                  Worum es geht
                </DropdownItem>
                {(snapshot.board.participantsEnabled || (snapshot.board.role === 'owner' && !me?.isGuest)) && (
                  <DropdownItem icon={<Contact size={16} />} onSelect={() => setPartOpen(true)}>
                    Beteiligte ohne Konto …
                  </DropdownItem>
                )}
                {snapshot.board.role === 'owner' && (
                  <DropdownItem icon={<Pencil size={16} />} onSelect={() => setRenameOpen(true)}>
                    Umbenennen
                  </DropdownItem>
                )}
                {snapshot.board.role === 'owner' && !snapshot.board.archivedAt && (
                  <DropdownItem icon={<FolderInput size={16} />} onSelect={() => setPlaceOpen(true)}>
                    Verschieben nach …
                  </DropdownItem>
                )}
                {snapshot.board.role === 'owner' && <DropdownSeparator />}
                <div className="min-[400px]:hidden">
                  <DropdownItem icon={<Users size={16} />} onSelect={() => setMembersOpen(true)}>
                    Mitglieder{othersOnline ? ` (${othersOnline} gerade da)` : ''}
                  </DropdownItem>
                </div>
                {canEdit && (
                  <DropdownItem icon={<Tag size={16} />} onSelect={() => setLabelsOpen(true)}>
                    Labels
                  </DropdownItem>
                )}
                {canEdit && !snapshot.board.archivedAt && (
                  <DropdownItem icon={<Upload size={16} />} onSelect={() => openCardImport({ boardId })}>
                    Karten importieren …
                  </DropdownItem>
                )}
                <DropdownItem icon={<Zap size={16} />} onSelect={() => setPanel('automations')}>
                  Automationen
                </DropdownItem>
                <DropdownItem icon={<LayoutTemplate size={16} />} onSelect={() => setPanel('cardTemplates')}>
                  Kartenvorlagen
                </DropdownItem>
                {canEdit && !me?.isGuest && (
                  <DropdownItem icon={<ClipboardList size={16} />} onSelect={() => setPanel('forms')}>
                    Formulare
                  </DropdownItem>
                )}
                <DropdownItem icon={<BellRing size={16} />} onSelect={() => setRemindersOpen(true)}>
                  Erinnerungen (Boardstandards)
                </DropdownItem>
                <DropdownSeparator />
                {!me?.isGuest && (
                  <DropdownItem icon={<CalendarDays size={16} />} onSelect={() => setPanel('calendar')}>
                    Kalender abonnieren
                  </DropdownItem>
                )}
                {snapshot.board.role === 'owner' && (
                  <DropdownItem icon={<Webhook size={16} />} onSelect={() => setPanel('webhooks')}>
                    Webhooks
                  </DropdownItem>
                )}
                {canEdit && !me?.isGuest && (
                  <DropdownItem icon={<BookmarkPlus size={16} />} onSelect={() => setPanel('saveTemplate')}>
                    Als Vorlage speichern …
                  </DropdownItem>
                )}
                {!me?.isGuest && (
                  <DropdownItem icon={<FileDown size={16} />} onSelect={() => setExportIds(null)}>
                    Exportieren …
                  </DropdownItem>
                )}
                {canEdit && !me?.isGuest && (
                  <DropdownItem
                    icon={<Download size={16} />}
                    onSelect={async () => {
                      try {
                        const res = await fetch(`/api/boards/${boardId}/export`, { credentials: 'same-origin' });
                        if (!res.ok) throw new Error('Export fehlgeschlagen.');
                        const url = URL.createObjectURL(await res.blob());
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `${snapshot.board.title.replace(/[\\/:*?"<>|]+/g, '').trim() || 'board'}.kaba.json`;
                        a.click();
                        setTimeout(() => URL.revokeObjectURL(url), 1000);
                        toast.info('KaBa-Datei gespeichert. Importieren über „Neues Board“ → „Importieren“ → „KaBa-Datei“.');
                      } catch (err) {
                        toast.error((err as Error).message);
                      }
                    }}
                  >
                    Als Datei exportieren
                  </DropdownItem>
                )}
                {snapshot.board.role === 'owner' && !snapshot.board.archivedAt && <DropdownSeparator />}
                {snapshot.board.role === 'owner' && !snapshot.board.archivedAt && (
                  <DropdownItem
                    icon={<Archive size={16} />}
                    onSelect={() => {
                      if (!window.confirm(`„${snapshot.board.title}“ archivieren? Es verschwindet aus den normalen Listen und bleibt unter „Archiv“ mit Ablage, Freigaben und Favoriten erhalten.`)) return;
                      boardAction.mutate(async () => {
                        await api.del(`/api/boards/${boardId}`);
                        refreshStructure();
                        toast.info(`„${snapshot.board.title}“ ist archiviert.`);
                        navigate('/');
                      });
                    }}
                  >
                    Archivieren
                  </DropdownItem>
                )}
                {snapshot.board.sourceTemplate && (
                  <>
                    <DropdownSeparator />
                    <p data-testid="board-source" className="max-w-72 px-2.5 py-2 text-xs text-[var(--ink-soft)]">
                      {sourceTemplateText(snapshot.board.sourceTemplate)}
                    </p>
                  </>
                )}
              </DropdownContent>
            </DropdownMenu>
          </div>
        </div>

        <div className="flex items-center gap-2 px-2 pt-1 pb-1.5 sm:px-3">
          <ViewSwitch layout={layout} onChange={setLayout} />
          {layout !== 'metrics' && layout !== 'overview' && layout !== 'files' && (
            <button
              type="button"
              onClick={() => setFilterOpen((o) => !o)}
              aria-expanded={filterOpen}
              aria-label={filterActive ? `Suchen und filtern (/) – ${activeFilterCount(filter)} aktiv` : 'Suchen und filtern (/)'}
              title="Suchen und filtern (/)"
              className={cx(
                'ml-auto inline-flex min-h-10 shrink-0 items-center gap-1.5 rounded-lg px-2.5 text-sm font-medium sm:min-h-9',
                filterOpen || filterActive ? 'bg-accent-soft text-[var(--ink)]' : 'text-[var(--ink-soft)] hover:bg-black/5 dark:hover:bg-white/10',
              )}
            >
              <ListFilter size={17} aria-hidden />
              <span className="hidden min-[400px]:inline">Filter</span>
              {filterActive && (
                <span className="inline-flex min-w-5 items-center justify-center rounded-full bg-accent px-1 text-xs leading-5 font-semibold text-on-accent" aria-hidden>
                  {activeFilterCount(filter)}
                </span>
              )}
            </button>
          )}
        </div>
        {layout !== 'metrics' && layout !== 'overview' && layout !== 'files' && filterOpen && <FilterBar snapshot={snapshot} view={layout} onClose={() => setFilterOpen(false)} />}
        {layout !== 'metrics' && layout !== 'overview' && layout !== 'files' && <ActiveFilters snapshot={snapshot} shownCount={shownCount} />}
        {(layout === 'board' || layout === 'list') && config.sort !== 'manual' && (
          <div className="flex items-center gap-2 border-t border-[var(--line)] bg-amber-50 px-3 py-1.5 text-xs text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
            <span className="flex-1">
              Reihenfolge nach {CARD_SORT_LABEL[config.sort]} – Ziehen verschiebt nur zwischen Spalten.
            </span>
            <button className="min-h-8 shrink-0 font-semibold underline" onClick={() => updateConfig({ ...config, sort: 'manual' })}>
              Manuell sortieren
            </button>
          </div>
        )}
        {layout === 'board' && (
          <nav aria-label="Spalten" className="flex gap-1.5 overflow-x-auto px-3 pb-2 sm:hidden">
            {columns.map((c) => (
              <button
                key={c.id}
                onClick={() => scrollToColumn(c.id)}
                className="min-h-9 shrink-0 rounded-full bg-black/5 px-3 text-sm dark:bg-white/10"
              >
                {c.title} <span className="text-[var(--ink-soft)]">{snapshot.cards.filter((x) => x.columnId === c.id && visible(x)).length}</span>
              </button>
            ))}
          </nav>
        )}
      </header>
      {snapshot.board.placement?.kind === 'visible' && (
        <nav aria-label="Ablage" className="border-b border-[var(--line)] px-4 py-1 text-xs text-[var(--ink-soft)] sm:px-5">
          <a
            href={`/?ort=bereich-${snapshot.board.placement.areaId}`}
            onClick={(e) => {
              e.preventDefault();
              navigate(`/?ort=bereich-${snapshot.board.placement!.areaId}`);
            }}
            className="underline-offset-2 hover:underline"
          >
            {snapshot.board.placement.areaName}
          </a>
          {snapshot.board.placement.folderId && (
            <>
              {' / '}
              <a
                href={`/?ort=ordner-${snapshot.board.placement.folderId}`}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(`/?ort=ordner-${snapshot.board.placement!.folderId}`);
                }}
                className="underline-offset-2 hover:underline"
              >
                {snapshot.board.placement.folderName}
              </a>
            </>
          )}
        </nav>
      )}
      {snapshot.board.archivedAt && (
        <div role="status" className="flex flex-wrap items-center gap-2 border-b border-[var(--line)] bg-black/5 px-4 py-2 text-sm dark:bg-white/5">
          <span className="flex-1">Dieses Board ist archiviert.</span>
          {snapshot.board.role === 'owner' && (
            <Button
              onClick={() =>
                boardAction.mutate(async () => {
                  await api.post(`/api/boards/${boardId}/restore`);
                  refreshStructure();
                  toast.info('Board wiederhergestellt.');
                })
              }
            >
              Wiederherstellen
            </Button>
          )}
        </div>
      )}
      {snapshot.board.ownerMissingSince && (
        <p role="status" className="border-b border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
          Dieses Board hat gerade keinen aktiven Besitzer und ist schreibgeschützt. Ein Teambesitzer bestimmt in den Teameinstellungen unter „Verwaltung“ einen neuen.
        </p>
      )}

      {/* ---------- Inhalt ---------- */}
      <main
        className="min-h-0 flex-1"
        // Hintergrund nur hinter Board und Liste; Tabelle, Kalender, Zeitleiste und Kennzahlen bleiben neutral (V04)
        style={layout === 'board' || layout === 'list' ? backgroundStyle(snapshot.branding?.background, themeMode, { hideDecor: me?.appearance?.hideDecor }) : undefined}
        data-board-background={layout === 'board' || layout === 'list' ? (snapshot.branding?.background.mode ?? 'neutral') : 'neutral'}
      >
        {layout === 'board' ? (
          <BoardDnd
            snapshot={snapshot}
            layout="board"
            lanes={lanes}
            sort={config.sort}
            visible={visible}
            canEdit={canEdit}
            onOpenCard={openCard}
            scrollerRef={scroller}
            className="kaba-board-scroll flex h-full items-start gap-3 overflow-x-auto overflow-y-hidden px-4 py-3 pb-24 sm:pb-4"
            renderColumn={(p) => <BoardColumn p={p} boardId={boardId} canEdit={canEdit} />}
            renderCard={({ card, dragging }) => (
              <CardTile card={card} labels={labels} members={members} fields={cardFields} dragging={dragging} density={config.density} viewers={viewerMembers.get(card.id)} menu={menu} waiting={depInfo.get(card.id)?.waiting} conflict={depInfo.get(card.id)?.conflict} />
            )}
            trailing={
              canEdit && (
                <button
                  onClick={() => setAddColumn(true)}
                  className="kaba-column flex min-h-12 w-[min(85vw,300px)] shrink-0 items-center gap-2 rounded-2xl border-2 border-dashed border-[var(--line)] bg-[var(--surface)] px-4 text-sm text-[var(--ink-soft)] hover:bg-[var(--column)] sm:w-[288px]"
                >
                  <Plus size={16} /> Spalte hinzufügen
                </button>
              )
            }
          />
        ) : layout === 'table' ? (
          <TableView snapshot={snapshot} visible={visible} canEdit={canEdit} onOpenCard={openCard} onExport={me?.isGuest ? undefined : (ids) => setExportIds(ids)} />
        ) : layout === 'files' ? (
          <FilesView boardId={boardId} teamId={snapshot?.board.teamId ?? null} canEdit={canEdit} guest={!!me?.isGuest} onOpenCard={openCard} />
        ) : layout === 'overview' ? (
          me?.isGuest ? (
            <p className="p-4 text-sm text-[var(--ink-soft)]">Die Übersicht steht Gästen nicht zur Verfügung.</p>
          ) : (
            <BoardOverview boardId={boardId} top={<ContextSection snapshot={snapshot} onEdit={() => openContext(boardId, true)} />} />
          )
        ) : layout === 'metrics' ? (
          <MetricsView snapshot={snapshot} onOpenCard={openCard} canEdit={canEdit} />
        ) : layout === 'timeline' ? (
          <TimelineView snapshot={snapshot} visible={visible} canEdit={canEdit} onOpenCard={openCard} />
        ) : layout === 'calendar' ? (
          <CalendarView snapshot={snapshot} visible={visible} canEdit={canEdit} onOpenCard={openCard} />
        ) : (
          <BoardDnd
            snapshot={snapshot}
            layout="list"
            lanes={lanes}
            sort={config.sort}
            visible={visible}
            canEdit={canEdit}
            onOpenCard={openCard}
            scrollerRef={scroller}
            className="mx-auto h-full max-w-3xl overflow-y-auto px-3 py-3 pb-28 sm:px-4"
            renderColumn={(p) => <ListGroup p={p} boardId={boardId} canEdit={canEdit} />}
            renderCard={({ card, dragging }) => (
              <ListRow card={card} labels={labels} members={members} fields={cardFields} dragging={dragging} density={config.density} menu={menu} waiting={depInfo.get(card.id)?.waiting} conflict={depInfo.get(card.id)?.conflict} />
            )}
          />
        )}
      </main>

      {/* ---------- Mobile: Schnell-Erfassen ---------- */}
      {canEdit && layout !== 'metrics' && layout !== 'overview' && layout !== 'files' && !keyboardOpen && (
        // Beschriftet statt nur „+“; verschwindet, solange die Bildschirmtastatur offen ist
        <button
          onClick={() => setQuickAdd(true)}
          className="fixed right-4 bottom-[max(1rem,env(safe-area-inset-bottom))] z-20 inline-flex min-h-12 items-center gap-2 rounded-full bg-accent pr-5 pl-4 text-base font-semibold text-on-accent shadow-xl active:scale-95 sm:hidden"
        >
          <Plus size={22} aria-hidden /> Neue Karte
        </button>
      )}

      <QuickAddSheet
        open={quickAdd}
        onClose={() => setQuickAdd(false)}
        boardId={boardId}
        columns={columns}
        defaultColumnId={currentColumn(scroller.current, columns.map((c) => c.id)) ?? columns[0]?.id}
      />
      {openCardId && (
        <Suspense fallback={null}>
          <CardDetailSheet
            snapshot={snapshot}
            cardId={openCardId}
            canEdit={canEdit}
            onClose={closeCard}
            onMove={(id) => setMoveCardId(id)}
          />
        </Suspense>
      )}
      <MoveDialog snapshot={snapshot} cardId={moveCardId} onClose={() => setMoveCardId(null)} />
      <LabelsDialog open={labelsOpen} snapshot={snapshot} onClose={() => setLabelsOpen(false)} />
      {canEdit && <CardImportDialog snapshot={snapshot} />}
      <AutomationsSheet snapshot={snapshot} open={panel === 'automations'} onClose={() => setPanel(null)} />
      <CardTemplatesSheet snapshot={snapshot} open={panel === 'cardTemplates'} onClose={() => setPanel(null)} />
      <SaveBoardTemplateDialog snapshot={snapshot} open={panel === 'saveTemplate'} onClose={() => setPanel(null)} />
      <CalendarFeedSheet boardId={snapshot.board.id} open={panel === 'calendar'} onClose={() => setPanel(null)} />
      {canEdit && !me?.isGuest && (
        <FormsSheet
          boardId={snapshot.board.id}
          columns={snapshot.columns}
          open={panel === 'forms'}
          onClose={() => {
            setPanel(null);
            if (new URLSearchParams(location.search).get('formulare')) setQuery({ formulare: null }, { replace: true });
          }}
        />
      )}
      {snapshot.board.role === 'owner' && <WebhooksSheet boardId={snapshot.board.id} open={panel === 'webhooks'} onClose={() => setPanel(null)} />}
      <MembersDialog snapshot={snapshot} open={membersOpen} onClose={() => setMembersOpen(false)} />
      {!me?.isGuest && <ExportDialog snapshot={snapshot} filter={config.filter} selectedIds={exportIds ?? undefined} open={exportIds !== undefined} onClose={() => setExportIds(undefined)} />}
      {snapshot.board.role === 'owner' && <BrandingDialog target={{ scope: 'board', boardId: snapshot.board.id, name: snapshot.board.title }} open={brandingOpen} onClose={() => setBrandingOpen(false)} />}
      <MoveBoardDialog board={placeOpen ? snapshot.board : null} onClose={() => setPlaceOpen(false)} />
      <BoardReminders
        open={remindersOpen}
        onClose={() => setRemindersOpen(false)}
        ctx={{ boardId, cardId: null, columns: snapshot.columns, canEditCard: canEdit, isOwner: snapshot.board.role === 'owner', meId: me?.id }}
      />
      <ContextSheet open={!!ctxOpen} edit={!!ctxOpen?.edit} onClose={() => setCtxOpen(null)} snapshot={snapshot} />
      <ParticipantsSheet open={partOpen} onClose={() => setPartOpen(false)} snapshot={snapshot} />
      <BoardSwitcher open={switcher} onClose={() => setSwitcher(false)} currentId={boardId} />
      <PromptDialog
        open={renameOpen}
        title="Board umbenennen"
        label="Name"
        initial={snapshot.board.title}
        onClose={() => setRenameOpen(false)}
        onSubmit={(v) => {
          setRenameOpen(false);
          if (v.trim()) boardAction.mutate(() => api.patch(`/api/boards/${boardId}`, { title: v.trim() }));
        }}
      />
      <PromptDialog
        open={addColumn}
        title="Neue Spalte"
        label="Name"
        confirm="Anlegen"
        onClose={() => setAddColumn(false)}
        onSubmit={(v) => {
          setAddColumn(false);
          if (v.trim()) boardAction.mutate(() => api.post(`/api/boards/${boardId}/columns`, { title: v.trim() }));
        }}
      />
    </div>
  );
}

/** Spalte, die im horizontalen Board gerade links sichtbar ist */
function currentColumn(scroller: HTMLElement | null, ids: string[]): string | undefined {
  if (!scroller) return undefined;
  const left = scroller.getBoundingClientRect().left;
  let best: { id: string; d: number } | undefined;
  for (const id of ids) {
    const el = document.getElementById(`col-${id}`);
    if (!el) continue;
    const d = Math.abs(el.getBoundingClientRect().left - left - 16);
    if (!best || d < best.d) best = { id, d };
  }
  return best?.id;
}

function QuickAddSheet({
  open,
  onClose,
  boardId,
  columns,
  defaultColumnId,
}: {
  open: boolean;
  onClose: () => void;
  boardId: string;
  columns: { id: string; title: string }[];
  defaultColumnId?: string;
}) {
  const [title, setTitle] = useState('');
  const [columnId, setColumnId] = useState(defaultColumnId ?? '');
  const [busy, setBusy] = useState(false);
  const [multi, setMulti] = useState<{ parsed: ParsedImport; text: string } | null>(null);
  const photo = useRef<HTMLInputElement>(null);
  const create = useCreateCard(boardId);
  useEffect(() => {
    if (open) {
      setColumnId(defaultColumnId ?? columns[0]?.id ?? '');
      setTitle('');
      setMulti(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const photoCard = async (file: File) => {
    setBusy(true);
    try {
      const card = await api.post<CardSummary>(`/api/boards/${boardId}/cards`, {
        columnId,
        title: title.trim() || `Foto vom ${new Date().toLocaleDateString('de-DE')}`,
        opId: newOpId(),
      });
      const form = new FormData();
      form.append('file', file);
      form.append('asCover', '1');
      await api.upload(`/api/cards/${card.id}/attachments`, form);
      const fresh = await api.get<CardSummary>(`/api/cards/${card.id}`);
      upsertCards(queryClient, boardId, [fresh]);
      onClose();
    } catch (err) {
      toast.error(`Foto-Karte fehlgeschlagen: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()} title="Neue Karte" side="center">
      <form
        className="flex flex-col gap-3 p-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (!title.trim()) return;
          // 0.88: mehrere Zeilen → Rückfrage, ob mehrere Karten entstehen sollen
          const parsed = pastedTasks(title.trim());
          if (parsed) return setMulti({ parsed, text: title.trim() });
          create.mutate({ columnId, title: title.trim().replace(/\s*\n\s*/g, ' '), placement: 'top' });
          onClose();
        }}
      >
        {multi && (
          <PasteTasksPrompt
            boardId={boardId}
            columnId={columnId}
            parsed={multi.parsed}
            text={multi.text}
            placement="top"
            onDone={onClose}
            onSingle={() => {
              create.mutate({ columnId, title: multi.text.replace(/\s*\n\s*/g, ' ').slice(0, 500), placement: 'top' });
              onClose();
            }}
            onCancel={() => {
              setTitle(multi.text);
              setMulti(null);
            }}
          />
        )}
        <textarea
          autoFocus
          rows={2}
          hidden={!!multi}
          value={title}
          onPaste={(e) => {
            if (title.trim()) return;
            const text = e.clipboardData.getData('text/plain');
            const parsed = pastedTasks(text);
            if (!parsed) return;
            e.preventDefault();
            setMulti({ parsed, text });
          }}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Was ist zu tun?"
          className="w-full resize-none rounded-xl border border-[var(--line)] bg-transparent p-3 text-base"
        />
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">Spalte</span>
          <select
            value={columnId}
            onChange={(e) => setColumnId(e.target.value)}
            className="min-h-11 rounded-lg border border-[var(--line)] bg-[var(--panel)] px-2 text-base"
          >
            {columns.map((c) => (
              <option key={c.id} value={c.id}>
                {c.title}
              </option>
            ))}
          </select>
        </label>
        <div className="flex gap-2">
          <Button type="button" onClick={() => photo.current?.click()} disabled={busy}>
            <Camera size={17} /> Foto als Karte
          </Button>
          <Button type="submit" variant="primary" className="flex-1" disabled={!title.trim() || busy}>
            Anlegen
          </Button>
        </div>
        <input
          ref={photo}
          type="file"
          accept="image/*"
          capture="environment"
          hidden
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void photoCard(f);
            e.target.value = '';
          }}
        />
      </form>
    </Sheet>
  );
}

/** Ansichten: die häufigen direkt, der Rest unter „Weitere“. Eine gewählte weitere Ansicht steht im Knopf. */
function ViewSwitch({ layout, onChange }: { layout: View; onChange: (v: View) => void }) {
  const phone = deviceClass() === 'phone';
  const primary = phone ? PRIMARY_PHONE : PRIMARY_DESKTOP;
  const more = VIEWS.filter((v) => !primary.includes(v));
  const moreActive = more.includes(layout);
  const [MoreIcon] = [moreActive ? VIEW_META[layout][1] : LayoutGrid];
  return (
    <div role="group" aria-label="Ansicht" className="flex min-w-0 items-center gap-0.5 rounded-lg bg-black/5 p-0.5 dark:bg-white/10">
      {primary.map((v) => {
        const [label, Icon] = VIEW_META[v];
        return (
          <button
            key={v}
            type="button"
            aria-pressed={layout === v}
            onClick={() => onChange(v)}
            title={v === 'board' || v === 'list' ? `${label} (Strg/Cmd+B wechselt)` : label}
            className={cx(
              'inline-flex min-h-10 items-center gap-1.5 rounded-md px-2 text-sm sm:min-h-8 sm:px-2.5',
              layout === v ? 'bg-[var(--panel)] font-medium shadow-sm' : 'text-[var(--ink-soft)] hover:text-[var(--ink)]',
            )}
          >
            <Icon size={16} aria-hidden />
            {label}
          </button>
        );
      })}
      <DropdownMenu>
        <DropdownTrigger asChild>
          <button
            type="button"
            aria-label={moreActive ? `Weitere Ansichten – gerade ${VIEW_META[layout][0]}` : 'Weitere Ansichten'}
            className={cx(
              'inline-flex min-h-10 items-center gap-1.5 rounded-md px-2.5 text-sm whitespace-nowrap sm:min-h-8',
              moreActive ? 'bg-[var(--panel)] font-medium shadow-sm' : 'text-[var(--ink-soft)] hover:text-[var(--ink)]',
            )}
          >
            <MoreIcon size={16} aria-hidden />
            <span className={moreActive ? '' : 'hidden min-[400px]:inline'}>{moreActive ? VIEW_META[layout][0] : 'Weitere'}</span>
            <ChevronDown size={14} aria-hidden />
          </button>
        </DropdownTrigger>
        <DropdownContent align="start">
          {more.map((v) => {
            const [label, Icon] = VIEW_META[v];
            return (
              <DropdownItem key={v} icon={<Icon size={16} />} onSelect={() => onChange(v)}>
                <span className="flex-1">{label}</span>
                {layout === v && <Check size={15} className="text-accent-fg" aria-label="aktiv" />}
              </DropdownItem>
            );
          })}
        </DropdownContent>
      </DropdownMenu>
    </div>
  );
}

/** 0.67: Herkunft des Boards (Vorlage und Fassung zum Zeitpunkt der Anlage – spätere Fassungen ändern das Board nicht) */
function sourceTemplateText(src: { id: string; version: number }): string {
  const t = BUILTIN_TEMPLATES.find((x) => x.id === src.id);
  if (!t) return 'Aus einer eigenen Vorlage angelegt';
  return `Aus der Vorlage „${t.name}“ (Fassung ${src.version}${(t.version ?? 1) > src.version ? ', inzwischen gibt es eine neuere' : ''})`;
}
